import {Box, Octicon, Timeline} from '@primer/react'
import type {TimelineRowEventActor$data, TimelineRowEventActor$key} from './__generated__/TimelineRowEventActor.graphql'
import {Ago} from './Ago'
import {EventActor, EventActorBase} from './EventActor'
import type {Icon} from '@primer/octicons-react'
import {useSlots} from '@primer/react/experimental'
import type React from 'react'
import {graphql, useFragment} from 'react-relay'
import type {TimelineEventExtraProps} from '../LockedEvent'

type TimelineRowType = {
  actor: TimelineRowEventActor$key | null | undefined
  timelineEventProps?: TimelineEventExtraProps
  highlighted: boolean
  createdAt: string
  deepLinkUrl: string
  showAgoTimestamp?: boolean
  showActorName?: boolean
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
  children: React.ReactNode
  leadingIcon: Icon
  iconColoring?: {
    color?: string
    backgroundColor?: string
  }
  leadingIconOnTop?: boolean
}

type RowInternalType = Omit<TimelineRowType, 'children' | 'actor'> & {
  slots: RowSlots
  actor: TimelineRowEventActor$key
}

type EventRowSlotsConfig = {
  trailing: typeof Trailing
  secondary: typeof Secondary
  main: typeof Main
}

type RowSlots = ReturnType<typeof useSlots<EventRowSlotsConfig>>

const Row = ({actor, children, ...props}: TimelineRowType) => {
  const slots = useSlots<EventRowSlotsConfig>(children, {
    trailing: Trailing,
    secondary: Secondary,
    main: Main,
  })

  if (actor) return <RowInternal actor={actor} slots={slots} {...props} />

  return <RowBase slots={slots} {...props} />
}

const RowInternal = ({actor, ...props}: RowInternalType) => {
  const actorData = useFragment(
    graphql`
      fragment TimelineRowEventActor on Actor {
        ...EventActor
      }
    `,
    actor,
  )

  return <RowBase {...props} actorData={actorData} />
}

type RowBaseType = Omit<TimelineRowType, 'children' | 'actor'> & {
  slots: RowSlots
  actorData?: TimelineRowEventActor$data | null
  timelineEventProps?: TimelineEventExtraProps
}

export const RowBase = ({
  showActorName = true,
  showAgoTimestamp = true,
  highlighted,
  createdAt,
  deepLinkUrl,
  refAttribute,
  leadingIcon,
  leadingIconOnTop,
  iconColoring,
  slots,
  actorData,
  timelineEventProps,
}: RowBaseType) => {
  const highlightedStyle = highlighted
    ? {
        borderRadius: '50%',
        boxShadow: `0px 0px 0px 2px var(--fgColor-accent, var(--color-accent-fg)), 0px 0px 0px 4px var(--bgColor-accent-muted, var(--color-accent-subtle))`,
      }
    : {}

  return (
    <Timeline.Item
      sx={{
        py: 2,
      }}
      ref={highlighted ? refAttribute : null}
    >
      <Timeline.Badge
        sx={{
          backgroundColor: iconColoring?.backgroundColor,
          ...highlightedStyle,
        }}
      >
        {leadingIcon && (
          <Octicon
            icon={leadingIcon}
            sx={{
              color: iconColoring?.color,
              fontSize: '14px',
            }}
          />
        )}
      </Timeline.Badge>
      <Timeline.Body
        sx={{
          color: 'fg.subtle',
          fontSize: '14px',
          width: '100%',
          paddingRight: 5,
          display: 'flex',
          flexDirection: 'column',
          pl: 2,
        }}
      >
        <Box sx={{display: 'flex'}}>
          <Box sx={{flexGrow: 1, flexWrap: 'nowrap'}}>
            {actorData && (
              <EventActor leadingIconOnTop={leadingIconOnTop} actor={actorData} showAvatarOnly={!showActorName} />
            )}
            {timelineEventProps && (
              <EventActorBase
                leadingIconOnTop={leadingIconOnTop}
                login={timelineEventProps.repositoryOwnerLogin}
                avatarUrl={timelineEventProps.repositoryAvatarURL}
                showAvatarOnly={!showActorName}
              />
            )}
            {slots[0].main}
            {showAgoTimestamp && <Ago timestamp={new Date(createdAt)} linkUrl={deepLinkUrl} />}
          </Box>
          <Box sx={{fontSize: 0}}>{slots[0].trailing}</Box>
        </Box>
        <Box>{slots[0].secondary}</Box>
      </Timeline.Body>
    </Timeline.Item>
  )
}

function Trailing(props: React.PropsWithChildren) {
  return <Box data-trailing>{props.children}</Box>
}

function Secondary(props: React.PropsWithChildren) {
  return <Box data-secondary>{props.children}</Box>
}

function Main(props: React.PropsWithChildren) {
  return <>{props.children}</>
}

export const TimelineRow = Object.assign(Row, {
  Trailing,
  Secondary,
  Main,
})

try{ Row.displayName ||= 'Row' } catch {}
try{ RowInternal.displayName ||= 'RowInternal' } catch {}
try{ RowBase.displayName ||= 'RowBase' } catch {}
try{ Trailing.displayName ||= 'Trailing' } catch {}
try{ Secondary.displayName ||= 'Secondary' } catch {}
try{ Main.displayName ||= 'Main' } catch {}
try{ TimelineRow.displayName ||= 'TimelineRow' } catch {}